import { Info, Plus, RefreshCw } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ErrorState, KpiCard, PageHeader } from '@/components/app';
import { useExperiment } from './hooks/useExperiment';
import { FunnelTable, VariantsList, VariantDialog, ClonePlansDialog } from './components';
import { formatMoney } from '../Plans/planFormat';

export default function ExperimentPage() {
  const {
    variants,
    analytics,
    isLoading,
    loadError,
    isSubmitting,
    isVariantDialogOpen,
    editingVariant,
    variantForm,
    setVariantForm,
    openCreateVariant,
    openEditVariant,
    setIsVariantDialogOpen,
    submitVariant,
    isCloneDialogOpen,
    cloningVariant,
    cloneForm,
    setCloneForm,
    openClonePlans,
    setIsCloneDialogOpen,
    submitClone,
    activate,
    stopEnrollment,
    archive,
    remove,
    refresh,
  } = useExperiment();

  const hasActive = variants.some((v) => v.status === 'active');
  const daysRunning = analytics?.days_running;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Эксперимент"
        subtitle={
          <>
            A/B-тест прайсинга: разным людям — разные цены, решение по ARPU
            {daysRunning !== null && daysRunning !== undefined && (
              <>
                {' '}· идёт <span className="tabular-nums">{daysRunning}</span> дн.
              </>
            )}
          </>
        }
        actions={
          <>
            <Button variant="secondary" onClick={() => void refresh()} disabled={isLoading}>
              <RefreshCw className={cn(isLoading && 'animate-spin')} />
              Обновить
            </Button>
            <Button onClick={openCreateVariant}>
              <Plus />
              Создать вариант
            </Button>
          </>
        }
      />

      {loadError ? (
        <Card>
          <ErrorState message={loadError} onRetry={() => void refresh()} />
        </Card>
      ) : (
        <>
          {!isLoading && !hasActive && (
            <div className="flex items-start gap-3 rounded-card border border-border-soft bg-surface-alt px-4 py-3 text-sm text-text-2">
              <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
              <p className="m-0">
                Эксперимент выключен: активных вариантов нет, и все пользователи видят базовый прайс-лист — ровно то же,
                что и раньше. Тест начнётся в момент, когда вы нажмёте «Запустить набор». Назначения выдаются только
                тем, кто зарегистрировался после первого запуска.
              </p>
            </div>
          )}

          {/* Общая воронка «заказ → оплата»: если отвал здесь, ценовой тест бессмыслен */}
          {analytics && analytics.totals.orders_count > 0 && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KpiCard label="Назначено в тест" value={analytics.totals.assigned.toLocaleString('ru-RU')} />
              <KpiCard label="Заказов создано" value={analytics.totals.orders_count.toLocaleString('ru-RU')} />
              <KpiCard
                label="Заказ → оплата"
                value={`${analytics.totals.order_to_payment_rate}%`}
                hint="Если отвал здесь большой — проблема в платёжке, а не в цене"
              />
              <KpiCard label="Выручка теста" value={formatMoney(analytics.totals.revenue)} variant="hero" />
            </div>
          )}

          <Card className="overflow-hidden" role="region" aria-labelledby="exp-funnel">
            <div className="flex flex-col gap-1 px-[18px] py-4">
              <h2 id="exp-funnel" className="m-0 font-display text-[17px] font-bold">
                Воронка по вариантам
              </h2>
              <p className="m-0 text-[13px] text-text-2">
                Решает ARPU, а не конверсия: дешёвый вариант почти всегда выигрывает по конверсии и при этом может
                проиграть по деньгам. Группа, где меньше {analytics?.min_payments_for_decision ?? 8} оплат, помечена —
                по ней решение не принимается.
              </p>
            </div>
            <FunnelTable analytics={analytics} isLoading={isLoading} />
          </Card>

          <VariantsList
            variants={variants}
            onEdit={openEditVariant}
            onClonePlans={openClonePlans}
            onActivate={activate}
            onStopEnrollment={stopEnrollment}
            onArchive={archive}
            onDelete={remove}
          />
        </>
      )}

      <VariantDialog
        isOpen={isVariantDialogOpen}
        onOpenChange={setIsVariantDialogOpen}
        formData={variantForm}
        onFormDataChange={setVariantForm}
        onSubmit={submitVariant}
        isSubmitting={isSubmitting}
        isEditing={Boolean(editingVariant)}
      />

      <ClonePlansDialog
        isOpen={isCloneDialogOpen}
        onOpenChange={setIsCloneDialogOpen}
        variant={cloningVariant}
        variants={variants}
        formData={cloneForm}
        onFormDataChange={setCloneForm}
        onSubmit={submitClone}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
